import { useCallback, useRef, useState } from "react";
// Real Web Audio AnalyserNode on the visitor's own mic input (proposal §4.2: "once mic capture is
// wired, the visitor's own mic input while they're speaking" drives the orb). This is genuinely
// real-time audio analysis, not simulated -- it's the half of §4.2's audio-reactivity mechanism
// that doesn't depend on §4.3's TTS/real-time-voice architecture decision at all, so it's safe to
// ship now. Returns a 0..1 volume level, updated via requestAnimationFrame, plus start/stop
// controls and any permission/device error.
export function useMicLevel() {
    const [level, setLevel] = useState(0);
    const [active, setActive] = useState(false);
    const [error, setError] = useState(null);
    const streamRef = useRef(null);
    const audioCtxRef = useRef(null);
    const rafRef = useRef(null);
    const stop = useCallback(() => {
        if (rafRef.current !== null)
            cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        audioCtxRef.current?.close().catch(() => { });
        audioCtxRef.current = null;
        setActive(false);
        setLevel(0);
    }, []);
    const start = useCallback(async () => {
        setError(null);
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;
            const ctx = new AudioContext();
            audioCtxRef.current = ctx;
            const source = ctx.createMediaStreamSource(stream);
            const analyser = ctx.createAnalyser();
            analyser.fftSize = 256;
            source.connect(analyser);
            const data = new Uint8Array(analyser.frequencyBinCount);
            const tick = () => {
                analyser.getByteTimeDomainData(data);
                // RMS of the time-domain signal around its 128 midpoint -- a standard cheap volume
                // estimate, not a full FFT read, since the orb only needs overall loudness, not spectrum.
                let sumSquares = 0;
                for (let i = 0; i < data.length; i++) {
                    const v = (data[i] - 128) / 128;
                    sumSquares += v * v;
                }
                const rms = Math.sqrt(sumSquares / data.length);
                setLevel(Math.min(1, rms * 4));
                rafRef.current = requestAnimationFrame(tick);
            };
            rafRef.current = requestAnimationFrame(tick);
            setActive(true);
        }
        catch (err) {
            setError(err instanceof Error && err.name === "NotAllowedError" ? "Microphone permission denied." : "Couldn't access your microphone.");
            stop();
        }
    }, [stop]);
    return { level, active, error, start, stop };
}
